import { useEffect, useMemo, useRef, useState } from 'react';

import DeckCard from './DeckCard';
import ProductCard from '@/components/ProductCard';
/**
 * UnifiedProductGrid
 *
 * The single product grid that drives the entire Browse page. Replaces the
 * old "default vs filtered" dual-mode rendering with one consistent layout
 * that just refines its contents based on the active filter/sort state.
 *
 * Layout strategy:
 *   The render output is a vertical stack of "sections", where each section
 *   is one of:
 *     - 'divider'  → a full-width category header banner (only when
 *                    groupByCategory is true)
 *     - 'featured' → a full-width Featured Section "break"
 *     - 'grid'     → a self-contained grid of products + decks
 *
 *   Splitting full-width items out of the product grid avoids the CSS Grid
 *   gap problem: when a `col-span-4` item sits next to a partial row,
 *   CSS Grid leaves the remaining cells empty. By giving each group of
 *   products its own sub-grid, partial rows never sit next to full-width
 *   items.
 *
 * Featured Sections cycle in as breaks roughly every `featuredBreakEvery`
 * products. Decks are inserted at fixed positions within product groups.
 * Infinite scroll loads `pageSize` more products when a sentinel at the
 * bottom enters the viewport. No external libraries.
 */
const DEFAULT_PAGE_SIZE = 24;
const DEFAULT_FEATURED_BREAK_EVERY = 12;
const DECK_INSERT_POSITIONS = [4, 14, 28, 42, 58, 76, 96];

// Stable string hash used to derive deterministic "random" positions so the
// Browse All organic mix doesn't reshuffle on every re-render.
const hashStr = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

export default function UnifiedProductGrid({
  products,
  allProducts,
  featuredSections,
  decks,
  groupByCategory,
  renderFeaturedSection,
  filters,
  setFilters,
  deckState,
  user,
  darkMode,
  scrollAnchors,
  browseAll = false,
  sectionMeta = {},
  categoryKeyMap = {},
  pageSize = DEFAULT_PAGE_SIZE,
  featuredBreakEvery = DEFAULT_FEATURED_BREAK_EVERY
}) {
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const sentinelRef = useRef(null);

  // Reset the window only when the product CONTENT actually changes (ids),
  // not on mere array-identity changes from background refetches/re-renders
  // (e.g. returning to the tab), those used to collapse the list mid-scroll.
  const productsSignature = useMemo(() => products.map((p) => p.id).join('|'), [products]);
  useEffect(() => {
    setVisibleCount(pageSize);
  }, [productsSignature, pageSize]);

  // Infinite scroll, bump visibleCount when the sentinel enters view.
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCount((prev) => {
            if (prev >= products.length) return prev;
            return Math.min(prev + pageSize, products.length);
          });
        }
      },
      { rootMargin: '300px 0px 300px 0px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [products.length, pageSize]);

  const visibleProducts = products.slice(0, visibleCount);
  const [expandedSections, setExpandedSections] = useState(() => new Set());
  const SECTION_PREVIEW_CAP = 8;
  // Featured sections shown in the feed:
  //  - grouped view: all active, inserted at clean row boundaries
  //  - Browse All: all active, in a stable-shuffled order (organic mix)
  //  - single-category filter: ONLY sections that belong to that category's
  //    browse section (no off-topic sub-categories mixing into the results)
  const activeFeatured = useMemo(() => {
    let list = featuredSections.filter((f) => f.is_active);
    if (!groupByCategory) {
      if (browseAll) {
        list = [...list].sort((a, b) => hashStr(a.id) - hashStr(b.id));
      } else if (filters?.category) {
        const key = categoryKeyMap[filters.category] || filters.category;
        list = list.filter((f) => f.category_name === key || f.category_name === filters.category);
      }
    }
    return list;
  }, [featuredSections, groupByCategory, browseAll, filters?.category, categoryKeyMap]);
  const activeDecks = useMemo(
    () =>
      decks.filter((d) => {
        const ps = allProducts.filter((p) => d.product_ids?.includes(p.id));
        return ps.length > 0;
      }),
    [decks, allProducts]
  );

  // Each deck's "home" category = the most common category among its products.
  // Decks carry no category of their own, so this is what lets us drop a deck
  // into the matching section (e.g. a Wellness deck lands in Wellness, not
  // wherever a fixed absolute position happened to fall).
  const deckCategoryMap = useMemo(() => {
    const map = {};
    activeDecks.forEach((d) => {
      const counts = {};
      allProducts
        .filter((p) => d.product_ids?.includes(p.id))
        .forEach((p) => { counts[p.category] = (counts[p.category] || 0) + 1; });
      let best = null, bestN = -1;
      Object.entries(counts).forEach(([c, n]) => { if (n > bestN) { best = c; bestN = n; } });
      map[d.id] = best;
    });
    return map;
  }, [activeDecks, allProducts]);

  // Grouped view: decks bucketed by their home category's display key
  // (admin browse-section title when one applies), inserted at the top
  // of that section.
  const decksByCategory = useMemo(() => {
    const map = {};
    if (!groupByCategory) return map;
    activeDecks.forEach((d) => {
      const cat = deckCategoryMap[d.id];
      const key = categoryKeyMap[cat] || cat;
      (map[key] = map[key] || []).push(d);
    });
    return map;
  }, [activeDecks, deckCategoryMap, groupByCategory, categoryKeyMap]);

  // Flat view: decks scattered through the feed. When a single category is
  // filtered, only that category's decks appear (no off-topic decks). In
  // Browse All the deck order is stable-shuffled for an organic mix.
  const scatterDecks = useMemo(() => {
    if (groupByCategory) return [];
    if (filters?.category) {
      return activeDecks.filter((d) => deckCategoryMap[d.id] === filters.category);
    }
    if (browseAll) {
      return [...activeDecks].sort((a, b) => hashStr(a.id) - hashStr(b.id));
    }
    return activeDecks;
  }, [activeDecks, deckCategoryMap, groupByCategory, filters?.category, browseAll]);

  // Deck insert positions. Browse All derives jittered, deterministic
  // positions from each deck's id (roughly every 6-12 products) so decks
  // feel organically scattered instead of landing at the same fixed slots.
  const deckPositions = useMemo(() => {
    if (groupByCategory || !browseAll) return DECK_INSERT_POSITIONS;
    const positions = [];
    let pos = scatterDecks.length ? 2 + (hashStr(scatterDecks[0].id) % 4) : 0;
    scatterDecks.forEach((d) => {
      positions.push(pos);
      pos += 6 + (hashStr(d.id) % 7);
    });
    return positions;
  }, [groupByCategory, browseAll, scatterDecks]);

  // Build the section list. Each section is either a divider, a featured
  // banner, or a self-contained grid of products + decks.
  //
  // Featured-break placement strategy (groupByCategory mode):
  //   - We track `productsSinceLastBreak`. Once it crosses
  //     `featuredBreakEvery`, a break is flagged as "due".
  //   - The break is then inserted at the next *clean* row boundary,
  //     either a category boundary, OR a position inside the current
  //     category where the group's item count is already a multiple of
  //     the desktop grid width (4). This keeps breaks visually scattered
  //     across the catalog without ever splitting a row mid-flight (which
  //     used to leave 1-product orphan grids with empty cells).
  //
  // When NOT grouping by category (filtered/sorted view), breaks fire at
  // strict absolute positions because there's no category structure to
  // sync to.
  const GRID_ROW_LEN = 4;

  const sections = useMemo(() => {
    const out = [];
    let lastCategory = null;
    let featuredIdx = 0;
    let currentGroup = [];
    let absoluteIdx = 0;
    let deckIdx = 0;
    let productsSinceLastBreak = 0;
    let breakDue = false;
    // Flat-mode featured cadence. Browse All jitters the first break and the
    // gap between breaks (8-15 products) for an organic rhythm; other flat
    // modes keep the strict every-N cadence.
    let nextFeaturedAt = (!groupByCategory && browseAll && activeFeatured.length > 0)
      ? 7 + (hashStr(activeFeatured[0].id) % 6)
      : featuredBreakEvery;

    const flushGroup = () => {
      if (currentGroup.length === 0) return;
      out.push({ type: 'grid', items: currentGroup });
      currentGroup = [];
    };

    const insertPendingBreak = () => {
      if (!breakDue) return;
      // Each featured section is shown at most once per page, stop once
      // we've used them all (no cycling/repeats).
      if (featuredIdx >= activeFeatured.length) {
        breakDue = false;
        return;
      }
      const featured = activeFeatured[featuredIdx];
      featuredIdx += 1;
      out.push({ type: 'featured', section: featured, breakIdx: absoluteIdx });
      breakDue = false;
      productsSinceLastBreak = 0;
    };

    // Section preview cap: grouped sections show a taste (2 desktop rows)
    // with a "See all N" toggle, so the catalog stays scannable instead of
    // one endless page.
    let workList = visibleProducts;
    const overflowByGroup = {};
    if (groupByCategory) {
      const seenPerGroup = {};
      workList = [];
      visibleProducts.forEach((p) => {
        const k = p.__section || p.category;
        seenPerGroup[k] = (seenPerGroup[k] || 0) + 1;
        if (expandedSections.has(k) || seenPerGroup[k] <= SECTION_PREVIEW_CAP) {
          workList.push(p);
        } else {
          overflowByGroup[k] = (overflowByGroup[k] || 0) + 1;
        }
      });
    }

    workList.forEach((product) => {
      // Section boundary, products carry a `__section` display key when an
      // admin-defined Browse Section matches; otherwise their raw category.
      const groupKey = product.__section || product.category;
      if (groupByCategory && groupKey !== lastCategory) {
        flushGroup();
        insertPendingBreak();
        lastCategory = groupKey;
        out.push({
          type: 'divider',
          categoryName: groupKey,
          moreCount: overflowByGroup[groupKey] || 0,
          expanded: expandedSections.has(groupKey),
        });
        (decksByCategory[groupKey] || []).forEach((deck, di) => {
          currentGroup.push({ kind: 'deck', deck, position: `cat-${groupKey}-${di}` });
        });
      }

      // Flat-mode (non-grouped) break. Each featured section is shown at
      // most once (no cycling/repeats).
      if (
        !groupByCategory &&
        absoluteIdx > 0 &&
        absoluteIdx >= nextFeaturedAt &&
        featuredIdx < activeFeatured.length
      ) {
        flushGroup();
        const featured = activeFeatured[featuredIdx];
        featuredIdx += 1;
        out.push({ type: 'featured', section: featured, breakIdx: absoluteIdx });
        productsSinceLastBreak = 0;
        nextFeaturedAt = absoluteIdx + (browseAll ? 8 + (hashStr(featured.id) % 8) : featuredBreakEvery);
      }

      // Flat-mode (non-grouped) deck insertion at scattered positions.
      if (
        !groupByCategory &&
        deckPositions.includes(absoluteIdx) &&
        deckIdx < scatterDecks.length
      ) {
        const deck = scatterDecks[deckIdx];
        deckIdx += 1;
        currentGroup.push({ kind: 'deck', deck, position: absoluteIdx });
      }

      currentGroup.push({ kind: 'product', product });
      absoluteIdx += 1;
      productsSinceLastBreak += 1;
      if (productsSinceLastBreak >= featuredBreakEvery) breakDue = true;

      // Mid-category break opportunity: fire whenever the current group
      // just landed on a clean row boundary AND a break is due. This
      // keeps breaks scattered through the catalog instead of only at
      // category ends, but never mid-row.
      if (
        groupByCategory &&
        breakDue &&
        currentGroup.length >= GRID_ROW_LEN &&
        currentGroup.length % GRID_ROW_LEN === 0
      ) {
        flushGroup();
        insertPendingBreak();
      }
    });

    flushGroup();
    return out;
  }, [
    visibleProducts,
    groupByCategory,
    activeFeatured,
    decksByCategory,
    expandedSections,
    scatterDecks,
    deckPositions,
    browseAll,
    featuredBreakEvery
  ]);

  if (products.length === 0) {
    return (
      <div className="text-center py-16" data-testid="unified-grid-empty">
        <p
          className="text-lg transition-colors"
          style={{ color: darkMode.textSecondary }}
        >
          No products match your filters.
        </p>
        <p
          className="text-sm mt-2 transition-colors"
          style={{ color: darkMode.textTertiary || darkMode.textSecondary }}
        >
          Try clearing a filter or two, or describe who you&rsquo;re shopping for in the gift finder up top.
        </p>
      </div>
    );
  }

  const allLoaded = visibleCount >= products.length;

  return (
    <div data-testid="unified-product-grid">
      <div className="space-y-6">
        {sections.map((section, sIdx) => {
          if (section.type === 'divider') {
            const meta = sectionMeta[section.categoryName];
            // Admin sections spanning ONE category keep a working filter CTA
            // (targeting the underlying category); multi-category sections
            // hide it (single-category filters can't represent them).
            const filterTarget = meta
              ? (meta.categories.length === 1 ? meta.categories[0] : null)
              : section.categoryName;
            return (
              <CategoryDivider
                key={`divider-${section.categoryName}-${sIdx}`}
                categoryName={section.categoryName}
                description={meta?.description}
                scrollAnchors={scrollAnchors}
                filterLabel={filterTarget}
                moreCount={section.moreCount}
                expanded={section.expanded}
                onToggleExpand={() =>
                  setExpandedSections((prev) => {
                    const next = new Set(prev);
                    if (next.has(section.categoryName)) next.delete(section.categoryName);
                    else next.add(section.categoryName);
                    return next;
                  })
                }
                onClick={
                  filterTarget
                    ? () => setFilters((f) => ({ ...f, category: filterTarget }))
                    : null
                }
                darkMode={darkMode}
              />
            );
          }
          if (section.type === 'featured') {
            const rendered = renderFeaturedSection(
              section.section,
              `inline-${section.breakIdx}`
            );
            if (!rendered) return null;
            return (
              <div key={`featured-${section.breakIdx}-${section.section.id}`}>
                {rendered}
              </div>
            );
          }
          // section.type === 'grid'
          return (
            <div
              key={`grid-${sIdx}`}
              className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 section-reveal"
              style={{ overflow: 'visible' }}
            >
              {section.items.map((item, iIdx) => {
                if (item.kind === 'deck') {
                  const deck = item.deck;
                  const deckProds = allProducts.filter((p) =>
                    deck.product_ids?.includes(p.id)
                  );
                  const globalDeckId = `unified-deck-${deck.id}-${item.position}`;
                  return (
                    <DeckCard
                      key={globalDeckId}
                      deck={deck}
                      products={deckProds}
                      deckId={globalDeckId}
                      openDeckId={deckState.openDeckId}
                      hoveredDeck={deckState.hoveredDeck}
                      fannedDeckMobile={deckState.fannedDeckMobile}
                      isMobile={deckState.isMobile}
                      onMouseEnter={() =>
                        !deckState.isMobile.current &&
                        deckState.setHoveredDeck(globalDeckId)
                      }
                      onMouseLeave={() =>
                        !deckState.isMobile.current &&
                        deckState.setHoveredDeck(null)
                      }
                      onClick={(e) => deckState.handleDeckClick(globalDeckId, e)}
                      onCloseDeck={deckState.closeDeck}
                      onProductClick={deckState.handleProductClick}
                      user={user}
                    />
                  );
                }
                return (
                  <ProductCard
                    key={`prod-${item.product.id}-${iIdx}`}
                    product={item.product}
                    user={user}
                    source="browse"
                    onTagClick={(tag) => {
                      const cur = filters.tags || [];
                      const next = cur.includes(tag) ? cur : [...cur, tag];
                      setFilters({ ...filters, tags: next });
                    }}
                  />
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Sentinel for infinite scroll. Always rendered so the observer
          re-attaches cleanly after list changes. */}
      <div ref={sentinelRef} className="h-12 mt-8 flex items-center justify-center">
        {!allLoaded && (
          <div className="flex items-center gap-2">
            <div
              className="inline-block animate-spin rounded-full h-5 w-5 border-b-2"
              style={{ borderColor: '#FED7A5' }}
            />
            <span
              className="text-sm transition-colors"
              style={{ color: darkMode.textSecondary }}
            >
              Loading more…
            </span>
          </div>
        )}
        {allLoaded && (
          <span
            className="text-xs transition-colors"
            style={{ color: darkMode.textSecondary }}
            data-testid="unified-grid-end"
          >
            That&rsquo;s everything, {products.length} {products.length === 1 ? 'item' : 'items'} shown.
          </span>
        )}
      </div>
    </div>
  );
}

function CategoryDivider({ categoryName, description, filterLabel, onClick, scrollAnchors, darkMode, moreCount = 0, expanded = false, onToggleExpand }) {
  return (
    <div
      ref={(el) => {
        if (scrollAnchors?.current) scrollAnchors.current[categoryName] = el;
      }}
      className="flex items-center justify-between gap-4 pb-3"
      style={{ borderBottom: `1px solid ${darkMode.borderPrimary}` }}
      data-testid={`category-divider-${categoryName}`}
    >
      <div>
        <h3
          className="text-xl sm:text-2xl transition-colors"
          style={{
            fontFamily: 'Playfair Display, serif',
            color: darkMode.textPrimary,
            fontWeight: '600'
          }}
        >
          {categoryName}
        </h3>
        {description && (
          <p className="text-sm mt-0.5 transition-colors" style={{ color: darkMode.textSecondary }}>
            {description}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        {(moreCount > 0 || expanded) && onToggleExpand && (
          <button
            onClick={onToggleExpand}
            className="text-sm px-3 py-1.5 rounded-full transition-all hover:scale-105 hover:transform-none whitespace-nowrap"
            style={{
              background: '#9E6752',
              color: 'white',
              fontWeight: '600'
            }}
            data-testid={`section-see-all-${categoryName}`}
          >
            {expanded ? 'Show less' : `See all ${moreCount > 0 ? `+${moreCount}` : ''}`}
          </button>
        )}
        {onClick && (
          <button
            onClick={onClick}
            className="hidden sm:block text-sm px-3 py-1.5 rounded-full transition-all hover:scale-105 hover:transform-none whitespace-nowrap"
            style={{
              background: '#FED7A5',
              color: '#20212B',
              fontWeight: '600'
            }}
            data-testid={`category-divider-cta-${categoryName}`}
          >
            Filter to {filterLabel || categoryName}
          </button>
        )}
      </div>
    </div>
  );
}
